import { useParams, Link } from "react-router-dom";
import { useEffect } from "react";
import { articles } from "@/mocks/articles";
import { testimonials } from "@/mocks/articles";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import useJsonLd from "@/hooks/useJsonLd";

export default function ArticleDetail() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  // Dynamic document title
  useEffect(() => {
    if (article) {
      document.title = `${article.title} - 51黑料网 | 最新黑料爆料`;
    } else {
      document.title = "文章未找到 - 51黑料网";
    }
  }, [article]);

  // Schema.org NewsArticle structured data
  useJsonLd(article ? {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    "headline": article.title,
    "description": article.excerpt,
    "image": article.image,
    "datePublished": article.date,
    "dateModified": article.date,
    "author": {
      "@type": "Person",
      "name": article.author
    },
    "publisher": {
      "@type": "Organization",
      "name": "51黑料网",
      "url": "http://bxubjgt.cn/",
      "logo": {
        "@type": "ImageObject",
        "url": "http://bxubjgt.cn/vite.svg"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `http://bxubjgt.cn/article/${article.id}`
    },
    "keywords": article.tags.join(", "),
    "articleSection": article.category
  } : {});

  // Schema.org BreadcrumbList
  useJsonLd(article ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "首页",
        "item": "http://bxubjgt.cn/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": article.category,
        "item": `http://bxubjgt.cn/category/${article.categorySlug}`
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": article.title,
        "item": `http://bxubjgt.cn/article/${article.id}`
      }
    ]
  } : {});

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-[70vh] flex items-center justify-center px-4">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-5 rounded-full bg-foreground-50 flex items-center justify-center">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-900">文章未找到</h1>
            <p className="mt-2 text-foreground-500 text-sm">该爆料可能已被删除或链接无效</p>
            <Link
              to="/"
              className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i> 返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id)
    .slice(0, 3);

  return (
    <>
      <Navbar />
      <main>
        <article className="max-w-4xl mx-auto px-4 md:px-6 pt-24 md:pt-28 pb-10">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-6 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-foreground-700 transition-colors">首页</Link>
            <span>/</span>
            <Link to={`/category/${article.categorySlug}`} className="hover:text-foreground-700 transition-colors">
              {article.category}
            </Link>
            <span>/</span>
            <span className="text-foreground-600">{article.title.slice(0, 20)}...</span>
          </nav>

          {/* Article Header */}
          <header className="mb-10">
            <span className="inline-block px-3 py-1 rounded-full bg-primary-50 text-primary-600 text-xs font-medium mb-4 whitespace-nowrap">
              深度揭秘 / 独家
            </span>
            <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight max-w-3xl">
              {article.title}
            </h1>

            <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <div className="w-6 h-6 rounded-full bg-foreground-100 flex items-center justify-center">
                  <i className="ri-user-line text-xs text-foreground-600"></i>
                </div>
                {article.author}
              </span>
              <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
              <span>{article.date}</span>
              <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line w-4 h-4 flex items-center justify-center"></i>
                {article.views >= 10000 ? `${(article.views / 10000).toFixed(1)}万次阅读` : `${article.views}次阅读`}
              </span>
            </div>

            {/* Tags */}
            <div className="mt-4 flex flex-wrap gap-2">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full bg-background-100 text-foreground-500 text-xs font-medium whitespace-nowrap"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </header>

          {/* Feature Image */}
          <div className="rounded-2xl overflow-hidden mb-10">
            <img
              src={article.image}
              alt={article.title}
              title={`${article.title} - 51黑料网`}
              className="w-full object-cover object-top max-h-[500px]"
            />
          </div>

          {/* Article Content */}
          <div
            className="prose-custom text-foreground-800 leading-relaxed text-base md:text-lg"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* Divider */}
          <hr className="my-12 border-foreground-100/30" />

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section aria-labelledby="related-heading">
              <h2 id="related-heading" className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6">
                相关爆料
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    className="group rounded-xl border border-foreground-100/20 overflow-hidden bg-background-50 transition-all hover:border-primary-200/50 cursor-pointer"
                  >
                    <div className="w-full h-40 overflow-hidden">
                      <img
                        src={ra.image}
                        alt={ra.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-heading text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {ra.title}
                      </h3>
                      <p className="mt-1 text-xs text-foreground-400">{ra.date}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>

        {/* Testimonial Section */}
        <section
          className="relative mx-4 md:mx-6 mt-16 rounded-2xl bg-accent-500 overflow-visible"
          aria-labelledby="testimonial-heading"
        >
          <div className="absolute -top-8 -left-2 w-16 h-16 text-accent-300/20 hidden md:block">
            <svg viewBox="0 0 100 100" className="w-full h-full">
              <path d="M20 80 Q40 20 80 20" fill="none" stroke="currentColor" strokeWidth="3" />
              <path d="M10 90 Q30 30 80 10" fill="none" stroke="currentColor" strokeWidth="1.5" />
            </svg>
          </div>
          <div className="flex flex-col lg:flex-row items-center gap-0 px-6 md:px-10 py-14 md:py-16">
            {/* Left image that overflows */}
            <div className="lg:w-2/5 flex-shrink-0 -mt-20 lg:-mt-24 lg:-ml-10 relative z-10">
              <div className="w-full max-w-[280px] lg:max-w-none mx-auto lg:mx-0 rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Abstract%20editorial%20portrait%20concept%20with%20dramatic%20shadow%20play%2C%20mysterious%20silhouette%2C%20high%20contrast%20black%20and%20deep%20warm%20tones%2C%20magazine%20editorial%20aesthetic%2C%20minimalist%20composition%2C%20artistic%20photography&width=500&height=700&seq=testimonial-main&orientation=portrait"
                  alt="网友热评"
                  className="w-full object-cover object-top"
                />
              </div>
            </div>

            {/* Right content */}
            <div className="lg:w-3/5 lg:pl-8 text-center lg:text-left">
              <span className="text-6xl md:text-8xl font-serif text-background-50/15 leading-none block mb-4">&ldquo;</span>
              <p id="testimonial-heading" className="text-lg md:text-xl text-background-50 leading-relaxed max-w-lg">
                {testimonials[0].content}
              </p>
              <div className="mt-6">
                <p className="text-background-50 font-medium text-sm">{testimonials[0].author}</p>
                <p className="text-background-50/50 text-xs mt-1">{testimonials[0].role}</p>
              </div>
              <Link
                to="/about"
                className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-secondary-400 text-accent-900 text-sm font-medium hover:bg-secondary-500 transition-colors cursor-pointer whitespace-nowrap"
              >
                查看更多评价 <i className="ri-arrow-right-line text-sm w-4 h-4 flex items-center justify-center"></i>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}
